import { api, type AnalyticsEvent, type AnalyticsEventName } from './api.js';

const CLIENT_VERSION = '0.1.0';
const BATCH_DELAY_MS = 250;
const MAX_BATCH_SIZE = 20;
const queue: AnalyticsEvent[] = [];
let timer: number | null = null;

const platform = (): AnalyticsEvent['platform'] => {
  const value = document.documentElement.dataset['platform'];
  return value === 'vk' ? 'VK' : value === 'max' ? 'MAX' : 'WEB';
};

const analyticsSessionId = (): string => {
  const key = 'bb_analytics_session';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(key, created);
    return created;
  } catch {
    return 'session-unavailable';
  }
};

const flush = (): void => {
  timer = null;
  if (!queue.length) return;
  const events = queue.splice(0, MAX_BATCH_SIZE);
  void api.analyticsEvents(events).catch(() => undefined);
  if (queue.length) timer = window.setTimeout(flush, BATCH_DELAY_MS);
};

export const trackAnalytics = (
  name: AnalyticsEventName,
  properties: AnalyticsEvent['properties'] = {},
): void => {
  let campaign: string | null = null;
  try {
    campaign = sessionStorage.getItem('bb:commerce-campaign');
  } catch {
    /* Optional session attribution. */
  }
  const requestedChatProduct =
    location.pathname === '/chat' ? new URLSearchParams(location.search).get('shop') : null;
  const chatProduct = ['vip', 'diamonds', 'cosmetics', 'cards'].includes(requestedChatProduct ?? '')
    ? requestedChatProduct
    : null;
  queue.push({
    name,
    platform: platform(),
    sessionId: analyticsSessionId(),
    clientVersion: CLIENT_VERSION,
    properties: {
      ...(campaign ? { campaign } : {}),
      ...(chatProduct ? { source: 'chat', chatProduct } : {}),
      ...properties,
    },
  });
  if (queue.length >= MAX_BATCH_SIZE) {
    if (timer !== null) window.clearTimeout(timer);
    flush();
  } else {
    timer ??= window.setTimeout(flush, BATCH_DELAY_MS);
  }
};

export type TutorialAnalyticsEventName = Extract<
  AnalyticsEventName,
  | 'tutorial_viewed'
  | 'tutorial_started'
  | 'tutorial_first_piece_selected'
  | 'tutorial_first_piece_placed'
  | 'tutorial_first_line_cleared'
  | 'tutorial_phase_completed'
  | 'tutorial_hint_shown'
  | 'tutorial_invalid_action'
  | 'tutorial_skipped'
  | 'tutorial_completed'
  | 'tutorial_arena_cta_clicked'
>;

export type TutorialAnalyticsProperties = {
  readonly phase: 1 | 2 | 3 | 'complete';
  readonly inputMethod?: 'drag' | 'tap' | 'keyboard';
  readonly elapsedMs: number;
  readonly hintCount: number;
  readonly invalidActionCount: number;
  readonly resumed: boolean;
  readonly reducedMotion: boolean;
};

export const trackTutorialAnalytics = (
  name: TutorialAnalyticsEventName,
  properties: TutorialAnalyticsProperties,
): void => trackAnalytics(name, properties);
